import React, { useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import { Box, Button, ButtonBase, Popover, Typography } from "@mui/material";

const ACCENT = "#26890d";
const INACTIVE_AMBER = "#F28C00";

const fmt = (n) => (n ?? 0).toLocaleString("en-AU");

// A zero is muted so the rows with real numbers stand out.
const DetailRow = ({ label, value }) => {
  const isZero = !value;
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
      <Typography sx={{ fontSize: "0.78rem", color: isZero ? "#aaa" : "#555" }}>{label}</Typography>
      <Typography sx={{ fontSize: "0.78rem", fontWeight: isZero ? 400 : 600, color: isZero ? "#aaa" : "#1a1a1a" }}>
        {fmt(value)}
      </Typography>
    </Box>
  );
};

// Seat total shown next to the plan badge; click opens a popover with the
// active/inactive split and the rest of copilot-info's seat_breakdown. A
// popover (not an inline expand) keeps the header's height — and everything
// below it — from shifting.
// onViewUsers (optional) adds a link to the active/inactive users page; omit it
// when the viewer isn't allowed to see that page.
const SeatBreakdownChip = ({ seatBreakdown, onViewUsers }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  if (!seatBreakdown || seatBreakdown.total == null) return null;

  const { total, active_this_cycle, inactive_this_cycle, added_this_cycle, pending_invitation, pending_cancellation } =
    seatBreakdown;
  const activePct = total > 0 ? Math.min(100, ((active_this_cycle ?? 0) / total) * 100) : 0;
  const inactivePct = total > 0 ? Math.min(100, ((inactive_this_cycle ?? 0) / total) * 100) : 0;
  const open = Boolean(anchorEl);

  return (
    <>
      <ButtonBase
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`${fmt(total)} Copilot seats, show breakdown`}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          px: 1.25,
          py: 0.3,
          borderRadius: "20px",
          border: "1px solid #bbb",
          backgroundColor: open ? "rgba(0,0,0,0.04)" : "transparent",
          "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
        }}
      >
        <PeopleAltOutlinedIcon sx={{ fontSize: 16, color: "#555" }} />
        <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#555", textTransform: "none" }}>
          {fmt(total)} seats
        </Typography>
        <ExpandMoreIcon
          sx={{ fontSize: 16, color: "#555", transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}
        />
      </ButtonBase>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { mt: 0.5, p: 2, width: 280, borderRadius: "10px" } }}
      >
        <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: "0.05em" }}>
          Seats (this billing cycle)
        </Typography>
        <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: "#1a1a1a", lineHeight: 1.3 }}>
          {fmt(total)} <Typography component="span" sx={{ fontSize: "0.8rem", color: "#888", fontWeight: 400 }}>total</Typography>
        </Typography>

        <Box sx={{ mt: 1, height: 8, borderRadius: 8, backgroundColor: INACTIVE_AMBER, overflow: "hidden" }}>
          <Box sx={{ width: `${activePct}%`, height: "100%", backgroundColor: ACCENT }} />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.8 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: ACCENT }} />
            <Typography sx={{ fontSize: "0.75rem", color: "#555" }}>
              Active {fmt(active_this_cycle)} ({activePct.toFixed(0)}%)
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: INACTIVE_AMBER }} />
            <Typography sx={{ fontSize: "0.75rem", color: "#555" }}>
              Inactive {fmt(inactive_this_cycle)} ({inactivePct.toFixed(0)}%)
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mt: 1.5, pt: 1, borderTop: "1px solid #f0f0f0" }}>
          <DetailRow label="Added this cycle" value={added_this_cycle} />
          <DetailRow label="Pending invitation" value={pending_invitation} />
          <DetailRow label="Pending cancellation" value={pending_cancellation} />
        </Box>

        {onViewUsers && (
          <Button
            size="small"
            fullWidth
            onClick={() => {
              setAnchorEl(null);
              onViewUsers();
            }}
            sx={{ mt: 1, textTransform: "none", fontSize: "0.78rem", fontWeight: 600, color: ACCENT, justifyContent: "flex-start", px: 0 }}
          >
            View active &amp; inactive users →
          </Button>
        )}
      </Popover>
    </>
  );
};

export default SeatBreakdownChip;
